import React, { Dispatch, SetStateAction } from 'react';
import {
  View, Text, StyleSheet, Platform, Pressable,
} from 'react-native';
import Moment from 'moment';
import TicketItemWrapper from './TicketItemWrapper';
import Colors from '../../../../../constants/Colors';
import isMobile from '../../../../../components/IsMobile';
import InProgressIcon from '../../../../../components/40pxIcons/InProgressIcon';
import PausedStatusIcon from '../../../../../components/40pxIcons/PausedStatusIcon';
import CompletedIcon from '../../../../../components/40pxIcons/CompletedIcon';
import FlagIcon40 from '../../../../../components/40pxIcons/FlagIcon';
import TicketArrowIcon from '../../../../../components/TicketArrowIcon';
import { logEvent } from '../../../../../components/LogEvent';
import getPriorityColor from '../../../../../components/PriorityColorScheme';
import {
  DEFAULT_TICKET_PRIORITY, Ticket, ticketPriorityIsValid, TicketStatus,
} from '../../../../../constants/Types';
import PinIcon from '../../../../../components/24pxIcons/PinIcon';

interface TicketItemProps {
  ticket: Ticket,
  selectedTicketId: string,
  setSelectedTicket: Dispatch<SetStateAction<Ticket>>,
  setModalVisible: Dispatch<SetStateAction<boolean>>,
  pinnedTickets: Ticket[],
  updatePinnedTickets: Dispatch<SetStateAction<Ticket[]>>,
  originalFetchTime,
  index: number,
}

const TicketItem = ({
  ticket, selectedTicketId, setSelectedTicket, setModalVisible, index, originalFetchTime,
  updatePinnedTickets, pinnedTickets,
}: TicketItemProps) => {
  const isSelected: boolean = Platform.OS === 'web' && ticket.id === selectedTicketId;
  const isPinned: boolean = (pinnedTickets ?? []).some((t: Ticket) => t.id === ticket.id);

  let { priority } = ticket;
  if (!ticketPriorityIsValid(priority)) priority = DEFAULT_TICKET_PRIORITY;

  return <TicketItemWrapper
    onPress={() => {
      logEvent('TicketItem', 'Ticket_Selected');
      setSelectedTicket(ticket);
      setModalVisible(true);
    }}
    selected={isSelected}
    style={[
      styles.listTicketItem,
      !isSelected && {
        backgroundColor: Moment(ticket.createdOn) > originalFetchTime
          ? Colors.light.hereworksWhite100 : '#fff',
        borderColor: Moment(ticket.createdOn) > originalFetchTime ? Colors.light.hereworksBlue300
          : Colors.light.hereworksWhite300,
      },
    ]}
    {...{ index }}
    status={ticket.status}
  >
    <View
      style={[
        styles.priorityCircle,
        { backgroundColor: getPriorityColor(priority) },
      ]}
    />
    {ticket.status === TicketStatus.INPROGRESS && <InProgressIcon
      style={styles.checkmarkStyle}
      selected={isSelected}
    />}
    {ticket.status === TicketStatus.PAUSED && <PausedStatusIcon
      style={styles.checkmarkStyle}
      selected={isSelected}
    />}
    {ticket.status === TicketStatus.COMPLETED && <CompletedIcon
      style={styles.checkmarkStyle}
      selected={isSelected}
    />}
    {ticket.status !== TicketStatus.INPROGRESS && ticket.status !== TicketStatus.PAUSED
      && ticket.status !== TicketStatus.COMPLETED
      && <FlagIcon40 style={styles.checkmarkStyle} />}
    <View style={styles.itemDetails}>
      <View style={{ flexDirection: 'row' }}>
        <Text
          numberOfLines={1}
          style={[
            styles.ticketTitle,
            isSelected && ticket.status !== TicketStatus.PAUSED && { color: '#FFF' },
          ]}
        >
          {ticket.name}
          {/* {ticket.images && ticket.images.length > 0 ? ' 📷' : ''} */}
        </Text>
        <Pressable style={(state) => [{
          backgroundColor: state.hovered ? Colors.light.hereworksBlue50 : 'transparent',
          padding: 2,
          borderRadius: 4,
        }]}
          onPress={() => {
            if (isPinned) updatePinnedTickets(pinnedTickets.filter((t) => t.id !== ticket.id));
            else updatePinnedTickets([...(pinnedTickets ?? []), ticket]);
          }}
        >
          <PinIcon fill={isPinned ? Colors.light.hereworksBlue200 : 'none'} />
        </Pressable>
      </View>
      <View style={{ flexDirection: 'row' }}>
        <Text
          style={[
            styles.ticketArea,
            isSelected && ticket.status !== TicketStatus.PAUSED && { color: '#FFF' },
          ]}
          numberOfLines={1}
        >
          {`${ticket.locationName ?? ''} | ${ticket.floorName ?? ''}`}
        </Text>
        {!isMobile && <Text
          style={[
            styles.ticketTime,
            isSelected && ticket.status !== TicketStatus.PAUSED && { color: '#FFF' },
          ]}>
          {Moment(ticket.createdOn).format('DD MMMM YYYY [at] hh:mm a')}
        </Text>}
      </View>
    </View>
    <TicketArrowIcon
      stroke={isSelected && ticket.status !== TicketStatus.PAUSED ? '#FFF' : undefined}
      style={styles.ticketArrowIcon}
    />
  </TicketItemWrapper>;
};

const styles = StyleSheet.create({
  shadow: {
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.05,
    shadowRadius: 20,
    shadowColor: 'black',
    elevation: 5,
  },
  ticketArea: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksPrimaryGrey,
    marginHorizontal: 14,
  },
  ticketTime: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    lineHeight: 18.75,
    color: Colors.light.hereworksBlack500,
    marginLeft: 10,
  },
  listTicketItem: {
    marginVertical: 6,
    height: 80,
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 2,
    borderColor: Colors.light.hereworksWhite300,
  },
  checkmarkStyle: {
    marginLeft: 20,
  },
  priorityCircle: {
    position: 'absolute',
    top: 9,
    left: 9,
    width: 14,
    height: 14,
    borderRadius: 7,
  },
  ticketTitle: {
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 16,
    color: Colors.light.hereworksPrimaryGrey,
    marginLeft: 14,
    paddingRight: 6,
    flex: 1,
  },
  ticketArrowIcon: {
    position: 'absolute',
    right: 20,
  },
  itemDetails: {
    flex: 1,
    marginRight: 32,
  },
});

export default TicketItem;
